/**
 * Criticidad de una alarma o umbral.
 * Ordena de mayor a menor gravedad y expone el color semántico del manual de marca.
 */

export const CRITICIDADES = ['critica', 'alta', 'media', 'baja'] as const;
export type CriticidadValor = (typeof CRITICIDADES)[number];

/** Mayor número = más grave. */
const PESO: Readonly<Record<CriticidadValor, number>> = {
  baja: 1,
  media: 2,
  alta: 3,
  critica: 4,
};

const ETIQUETA: Readonly<Record<CriticidadValor, string>> = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

/** Etiqueta corta para badges estrechos (móvil). */
const ETIQUETA_CORTA: Readonly<Record<CriticidadValor, string>> = {
  critica: 'CRÍT',
  alta: 'ALTA',
  media: 'MEDIA',
  baja: 'BAJA',
};

/** Nombre del token CSS definido en styles/tokens.css. */
const TOKEN: Readonly<Record<CriticidadValor, string>> = {
  critica: '--sev-critica',
  alta: '--sev-alta',
  media: '--sev-media',
  baja: '--sev-baja',
};

export class Criticidad {
  private constructor(readonly valor: CriticidadValor) {}

  /** Valor desconocido o ausente se trata como `media` (ni alarmista ni silencioso). */
  static from(raw: string | null | undefined): Criticidad {
    const limpio = (raw ?? '').trim().toLowerCase();
    const valor = (CRITICIDADES as readonly string[]).includes(limpio)
      ? (limpio as CriticidadValor)
      : 'media';
    return new Criticidad(valor);
  }

  static todas(): Criticidad[] {
    return CRITICIDADES.map((v) => new Criticidad(v));
  }

  get peso(): number {
    return PESO[this.valor];
  }

  get etiqueta(): string {
    return ETIQUETA[this.valor];
  }

  get etiquetaCorta(): string {
    return ETIQUETA_CORTA[this.valor];
  }

  /** `var(--sev-critica)` — listo para usar en un style inline o CSS var. */
  get color(): string {
    return `var(${TOKEN[this.valor]})`;
  }

  get esCritica(): boolean {
    return this.valor === 'critica';
  }

  /** Requiere atención inmediata: crítica o alta. */
  get requiereAtencionInmediata(): boolean {
    return this.peso >= PESO.alta;
  }

  alMenos(minima: CriticidadValor): boolean {
    return this.peso >= PESO[minima];
  }

  /** Comparador para ordenar de más grave a menos grave. */
  static comparar(a: Criticidad, b: Criticidad): number {
    return b.peso - a.peso;
  }

  toString(): string {
    return this.valor;
  }
}
